import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  LinearProgress,
  Autocomplete,
  Link,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Message as MessageIcon,
  AttachMoney as MoneyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { usePermissions, useDataProvider } from 'react-admin';
import { PERMISSIONS } from '../../constants';
import { useSupportAgent } from '../../hooks/useSupportAgent';

interface UsageReport {
  periodo: {
    inicio: string;
    fim: string;
  };
  resumo: {
    totalSessoes: number;
    totalMensagens: number;
    totalTokens: number;
    custoTotalUsd: number;
  };
  satisfacao: {
    positivos: number;
    negativos: number;
    taxa: number;
  };
  usoPorLlm: Array<{
    llm: string;
    mensagens: number;
    custo: number;
    percentual: number;
  }>;
  contextosMaisUsados: Array<{
    contexto: string;
    vezesUsado: number;
    percentual: number;
  }>;
  topUsuarios: Array<{
    odUserId: string;
    nome: string;
    mensagens: number;
    tokens: number;
    custo: number;
    taxaSatisfacao: number;
  }>;
  deepseekBalance: {
    available: number;
    currency: string;
  } | null;
}

interface Chain {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  name: string;
  login: string;
}

// Formata nome do LLM para exibição
const formatLlmName = (llm: string): string => {
  const mappings: Record<string, string> = {
    'deepseek': 'DeepSeek',
    'deepseek_minimal': 'DeepSeek (Minimal)',
    'deepseek_full': 'DeepSeek (Full)',
    'gemini': 'Gemini',
    'gemini_full': 'Gemini (Full)',
    'gemini_extended': 'Gemini (Extended)',
  };
  return mappings[llm?.toLowerCase()] || llm;
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}> = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}15`,
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SupportAgentReport: React.FC = () => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const supportAgent = useSupportAgent();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
  const [selectedUser, setSelectedUser] = useState<Employee | null>(null);
  const [adminOnly, setAdminOnly] = useState(false);

  // Dados para os selects
  const [chains, setChains] = useState<Chain[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingChains, setLoadingChains] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const hasPermission = permissions?.includes(PERMISSIONS.ADMIN);

  // Carrega lista de redes
  useEffect(() => {
    const loadChains = async () => {
      setLoadingChains(true);
      try {
        const result = await dataProvider.getList('chains', {
          filter: {},
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
        });
        setChains(result.data as Chain[]);
      } catch (err) {
        console.error('Erro ao carregar redes:', err);
      } finally {
        setLoadingChains(false);
      }
    };
    if (hasPermission) {
      loadChains();
    }
  }, [hasPermission, dataProvider]);

  // Carrega lista de funcionários (filtrado por rede ou admin)
  useEffect(() => {
    const loadEmployees = async () => {
      setLoadingEmployees(true);
      setSelectedUser(null); // Limpa usuário selecionado quando muda filtro
      try {
        const filter: Record<string, string> = {};
        if (adminOnly) {
          filter.type = 'admin';
        } else if (selectedChain) {
          filter.chainId = selectedChain.id;
        }
        const result = await dataProvider.getList('employees', {
          filter,
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
        });
        setEmployees(result.data as Employee[]);
      } catch (err) {
        console.error('Erro ao carregar funcionários:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };
    if (hasPermission) {
      loadEmployees();
    }
  }, [hasPermission, dataProvider, selectedChain, adminOnly]);

  // Limpa a rede selecionada quando ativa o filtro de admin
  useEffect(() => {
    if (adminOnly) {
      setSelectedChain(null);
    }
  }, [adminOnly]);

  // Busca o relatório automaticamente quando os filtros mudam
  useEffect(() => {
    const fetchReport = async () => {
      if (!hasPermission) return;

      setLoading(true);
      setError(null);
      try {
        const response = await supportAgent.getUsageReport(
          startDate,
          endDate,
          selectedUser?.id,
          selectedChain?.id
        );
        setReport(response.data);
      } catch (err: any) {
        setError(err?.message || 'Erro ao carregar relatório');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [hasPermission, startDate, endDate, selectedUser, selectedChain]);

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">
          Você não tem permissão para acessar este relatório.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <BotIcon sx={{ fontSize: 40, color: '#667eea' }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Relatório do Agente de Suporte
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Métricas de uso e performance do assistente IA
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Data Inicial"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <TextField
              label="Data Final"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 160 }}
            />
            <Autocomplete
              options={chains}
              getOptionLabel={(option) => option.name}
              value={selectedChain}
              onChange={(_, value) => setSelectedChain(value)}
              loading={loadingChains}
              disabled={adminOnly}
              size="small"
              sx={{ width: 200 }}
              renderInput={(params) => (
                <TextField {...params} label="Rede" placeholder="Todas as redes" />
              )}
            />
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => option.name || option.login}
              value={selectedUser}
              onChange={(_, value) => setSelectedUser(value)}
              loading={loadingEmployees}
              size="small"
              sx={{ width: 200 }}
              renderInput={(params) => (
                <TextField {...params} label="Usuário" placeholder="Todos os usuários" />
              )}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={adminOnly}
                  onChange={(e) => setAdminOnly(e.target.checked)}
                  size="small"
                />
              }
              label="Só admins"
            />
            {loading && <CircularProgress size={24} />}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Card sx={{ mb: 3, bgcolor: '#fee2e2', border: '1px solid #ef4444' }}>
          <CardContent>
            <Typography color="error">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total de Sessões"
                value={report.resumo?.totalSessoes || 0}
                icon={<BotIcon />}
                color="#667eea"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total de Mensagens"
                value={report.resumo?.totalMensagens || 0}
                icon={<MessageIcon />}
                color="#10b981"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total de Tokens"
                value={Number(report.resumo?.totalTokens || 0).toLocaleString('pt-BR')}
                icon={<PersonIcon />}
                color="#f59e0b"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Custo Total"
                value={`$${Number(report.resumo?.custoTotalUsd || 0).toFixed(4)}`}
                icon={<MoneyIcon />}
                color="#ef4444"
                subtitle={report.deepseekBalance
                  ? `Saldo DeepSeek: ${report.deepseekBalance.currency === 'CNY' ? '¥' : '$'}${Number(report.deepseekBalance.available).toFixed(2)}`
                  : undefined
                }
              />
            </Grid>
          </Grid>

          {/* Satisfaction Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Satisfação
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ThumbUpIcon sx={{ color: '#10b981', fontSize: 32 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="#10b981">
                        {report.satisfacao?.positivos || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Feedbacks Positivos
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ThumbDownIcon sx={{ color: '#ef4444', fontSize: 32 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="#ef4444">
                        {report.satisfacao?.negativos || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Feedbacks Negativos
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Taxa de Satisfação
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Number(report.satisfacao?.taxa || 0) * 100}
                        sx={{
                          flex: 1,
                          height: 10,
                          borderRadius: 5,
                          bgcolor: '#e5e7eb',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: (report.satisfacao?.taxa || 0) >= 0.7 ? '#10b981' : (report.satisfacao?.taxa || 0) >= 0.4 ? '#f59e0b' : '#ef4444',
                          },
                        }}
                      />
                      <Typography variant="h6" fontWeight="bold">
                        {(Number(report.satisfacao?.taxa || 0) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* LLM Usage */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Uso por LLM
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Modelo</TableCell>
                          <TableCell align="center">Mensagens</TableCell>
                          <TableCell align="right">Custo</TableCell>
                          <TableCell align="right">%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(report.usoPorLlm || []).map((llm) => (
                          <TableRow key={llm.llm}>
                            <TableCell>
                              <Chip label={formatLlmName(llm.llm)} size="small" />
                            </TableCell>
                            <TableCell align="center">{llm.mensagens}</TableCell>
                            <TableCell align="right">${Number(llm.custo || 0).toFixed(4)}</TableCell>
                            <TableCell align="right">{Number(llm.percentual || 0).toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Most Used Contexts */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Contextos Mais Usados
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Contexto</TableCell>
                          <TableCell align="center">Vezes Usado</TableCell>
                          <TableCell align="right">%</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(report.contextosMaisUsados || []).map((ctx) => (
                          <TableRow key={ctx.contexto}>
                            <TableCell>
                              <Chip label={ctx.contexto} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">{ctx.vezesUsado}</TableCell>
                            <TableCell align="right">{Number(ctx.percentual || 0).toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Users */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Top Usuários
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell align="center">Mensagens</TableCell>
                      <TableCell align="center">Tokens</TableCell>
                      <TableCell align="right">Custo</TableCell>
                      <TableCell align="right">Satisfação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.topUsuarios || []).map((user) => (
                      <TableRow key={user.odUserId}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#667eea' }} />
                            <Link
                              href={`#/employees/${user.odUserId}`}
                              underline="hover"
                              sx={{
                                color: '#667eea',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': { color: '#764ba2' },
                              }}
                            >
                              {user.nome || 'Usuário'}
                            </Link>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{user.mensagens}</TableCell>
                        <TableCell align="center">{Number(user.tokens || 0).toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">${Number(user.custo || 0).toFixed(4)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${(Number(user.taxaSatisfacao || 0) * 100).toFixed(0)}%`}
                            size="small"
                            sx={{
                              bgcolor: (user.taxaSatisfacao || 0) >= 0.7 ? '#d1fae5' : (user.taxaSatisfacao || 0) >= 0.4 ? '#fef3c7' : '#fee2e2',
                              color: (user.taxaSatisfacao || 0) >= 0.7 ? '#10b981' : (user.taxaSatisfacao || 0) >= 0.4 ? '#f59e0b' : '#ef4444',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default SupportAgentReport;
